import { useCallback } from 'react';
import { stringify } from 'querystring';

import { sendDeepLinkAction } from '@bucketplace/owap-sdk';
import { usePayConfigurationBridge } from '@bucketplace/payment-bridge-react';

import { PAYMENT_URL } from 'common';
import { useOwapInfo } from 'hooks';
import { usePlatformInfo } from './usePlatformInfo';

type OhousePayPageType =
  | 'home'
  | 'terms'
  | 'payment_method_registration'
  | 'setting_cash_receipt';

interface UseOhousePayConfigPageParams {
  pageType: OhousePayPageType;
  pgId?: string;
  onClose?: () => void;
}

interface UseOhousePayConfigPageReturns {
  openPage: (additionalData?: Record<string, string>) => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = (): void => {};

export const useOhousePayConfigPage = ({
  pageType,
  pgId,
  onClose = noop,
}: UseOhousePayConfigPageParams): UseOhousePayConfigPageReturns => {
  const { doPayConfigurationProcess } = usePayConfigurationBridge({
    // NOTE: PC에서만 동작
    onSuccess: onClose,
    onError: onClose,
    onFail: onClose,
  });

  const { isOwap } = useOwapInfo();
  const { osType } = usePlatformInfo();

  const openPage = useCallback((additionalData?: Record<string, string>) => {
    const ohousePayQueryString = {
      ...(pgId ? { pgId } : {}),
      ...(additionalData ? { additionalData: JSON.stringify(additionalData) } : {}),
    };
    const baseUrl = `${PAYMENT_URL}/ohouse-pay/${pageType}`;
    const qs = stringify(ohousePayQueryString);
    const url = qs ? `${baseUrl}?${qs}` : baseUrl;

    // PC
    if (osType === 'Web') {
      doPayConfigurationProcess(url);
      return;
    }

    // mWeb
    if (osType === 'mWebIOS' || osType === 'mWebAndroid') {
      const newWindow = window.open(url);
      const interval = setInterval(() => {
        if (!newWindow || newWindow?.closed) {
          clearInterval(interval);
          onClose();
        }
      }, 500);
      return;
    }

    // App - OWAP
    if (isOwap) {
      sendDeepLinkAction('move', {
        url,
        webviewType: 'fullmodal',
        isAllowOutboundLink: true,
      });
      return;
    }

    // App - iOS 구웹뷰
    if (osType === 'iOS') {
      sendDeepLinkAction('move', {
        url,
        webviewType: 'fullmodal',
        isAllowOutboundLink: true,
      });
      return;
    }

    // App - Android 구웹뷰
    if (osType === 'Android') {
      const owapQueryString = {
        webviewType: 'fullmodal',
        isAllowOutboundLink: true,
      };
      const owapQs = stringify({
        owap: 'true',
        ...owapQueryString,
        ...ohousePayQueryString,
      });
      const owapUrl = `${baseUrl}?${owapQs}`;
      window.open(owapUrl);
      return;
    }
  }, [pgId, pageType, osType, isOwap, doPayConfigurationProcess, onClose]);

  return {
    openPage,
  };
};
